import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL, scrollToId } from "../../lib/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 w-full z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/75 border-b border-white/10 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <button
          onClick={() => go("#home")}
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <img src="/kat-logo.png" alt="KAT Transportation Services logo" className="h-11 w-11 object-contain" />
          <div className="hidden sm:block text-left leading-none">
            <div className="font-serif-display text-lg tracking-wide text-gold-gradient">KAT</div>
            <div className="text-[10px] tracking-[0.28em] text-neutral-400 uppercase mt-0.5">Transportation</div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="text-sm text-neutral-300 hover:text-[#D4AF37] transition-colors duration-300 tracking-wide relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#D4AF37] after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="navbar-call-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black px-5 py-2.5 text-sm font-semibold hover:bg-[#f3e5ab] transition-colors duration-300"
          >
            <Phone size={15} strokeWidth={2.5} />
            {PHONE_DISPLAY}
          </a>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                  onClick={() => go(l.href)}
                  className="text-left py-3 text-lg font-serif-display text-neutral-200 hover:text-[#D4AF37] transition-colors border-b border-white/5"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="mobile-call-button"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black px-5 py-3 font-semibold"
              >
                <Phone size={16} /> Call or Text {PHONE_DISPLAY}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
